/**
 * ProfileBoostPromoModal — Iter-221 P1 visibility/conversion enhancement.
 *
 * Tasteful one-shot popup that surfaces the $4.99 Profile Boost product
 * the FIRST time a talent loads /talent/home each day. Dismissed via
 * localStorage cooldown (24h) so it's promotional but never spammy.
 *
 * Auto-suppressed when:
 *   • The talent already has an active boost (status?.has_active_boost)
 *   • The talent has dismissed within the last 24h
 *   • The talent has dismissed permanently (the "Don't show again" check)
 *
 * Mount on the talent home dashboard only.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, Eye, TrendingUp, Zap, Check } from 'lucide-react';
import { monetizationAPI } from '../utils/api';
import { introShownThisSession, markIntroShownThisSession } from '../utils/introSession';

const STORAGE_KEY = 'ievntur_boost_promo_dismissed_until';
const PERMANENT_KEY = 'ievntur_boost_promo_dismissed_permanently';
const COOLDOWN_HOURS = 24;

const ProfileBoostPromoModal = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let listener = null;
    const tryOpen = async () => {
      try {
        // Iter-234 — defer if the "What's New" walkthrough is in flight or
        // currently open. We re-check on `ievntur:whats-new-closed` so this
        // promo still shows on the same page load once the walkthrough is
        // dismissed.
        if (typeof window !== 'undefined' && window.__ievnturWhatsNewActive) {
          if (!listener) {
            listener = () => { if (!cancelled) tryOpen(); };
            window.addEventListener('ievntur:whats-new-closed', listener);
          }
          return;
        }
        // Permanently dismissed → never show.
        if (localStorage.getItem(PERMANENT_KEY) === '1') return;
        // Within cooldown window → skip this load.
        const until = localStorage.getItem(STORAGE_KEY);
        if (until && Number(until) > Date.now()) return;
        // Suppress if user already has an active boost.
        const res = await monetizationAPI.getHighlightStatus();
        if (cancelled) return;
        const status = res?.data;
        if (status?.has_active_boost || status?.active_boost?.is_active) return;
        // One-intro-per-session: if the Getting-Started video or Community
        // intro already showed this login, don't stack another pop-up.
        if (introShownThisSession()) return;
        // Slight delay so the modal doesn't fight the dashboard's
        // initial render — feels polished, not jarring.
        setTimeout(() => { if (!cancelled) { markIntroShownThisSession(); setOpen(true); } }, 1400);
      } catch {
        /* Silently skip — the promo is non-critical. */
      }
    };
    tryOpen();
    return () => {
      cancelled = true;
      if (listener) window.removeEventListener('ievntur:whats-new-closed', listener);
    };
  }, []);

  const dismiss = () => {
    const until = Date.now() + COOLDOWN_HOURS * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY, String(until));
    if (dontShowAgain) localStorage.setItem(PERMANENT_KEY, '1');
    setOpen(false);
  };

  const goToBoost = () => {
    if (dontShowAgain) localStorage.setItem(PERMANENT_KEY, '1');
    // Treat clicking the CTA as engaging — same 24h cooldown for future visits.
    localStorage.setItem(STORAGE_KEY, String(Date.now() + COOLDOWN_HOURS * 60 * 60 * 1000));
    setOpen(false);
    navigate('/talent/profile-boost');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden border-0"
        data-testid="profile-boost-promo-modal"
      >
        {/* Gradient header — keeps the promo on-brand without screaming. */}
        <div className="bg-gradient-to-br from-amber-400 via-pink-500 to-purple-600 text-white p-6">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5" />
            <span className="text-xs font-semibold uppercase tracking-wider">Profile Boost</span>
          </div>
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="text-2xl font-bold text-white">
              Get seen by more clients
            </DialogTitle>
            <DialogDescription className="text-white/90 text-sm">
              A 24-hour boost lifts your profile to the top of Discovery for just $4.99 — typically 3–5× the views.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-4">
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-sm text-gray-700" data-testid="boost-benefit-views">
              <Eye className="w-4 h-4 text-pink-500 mt-0.5 shrink-0" />
              <span><strong>Top of Discovery</strong> for 24 hours — clients see you first</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-700" data-testid="boost-benefit-bookings">
              <TrendingUp className="w-4 h-4 text-pink-500 mt-0.5 shrink-0" />
              <span><strong>3–5× more profile views</strong> based on platform averages</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-700" data-testid="boost-benefit-instant">
              <Zap className="w-4 h-4 text-pink-500 mt-0.5 shrink-0" />
              <span><strong>Activates instantly</strong> — no waiting, no commitment</span>
            </li>
          </ul>

          <div className="flex items-baseline gap-2 pt-2">
            <span className="text-3xl font-bold text-gray-900" data-testid="boost-price">$4.99</span>
            <span className="text-sm text-gray-500">/ 24 hours</span>
          </div>

          {/* Iter-225n — explicit free-to-work disclaimer */}
          <p
            className="text-xs text-gray-600 leading-snug bg-gray-50 border border-gray-200 rounded-md px-3 py-2"
            data-testid="boost-free-disclaimer"
          >
            Applying to gigs and getting booked is always free. This optional boost simply increases profile visibility.
          </p>

          <div className="flex flex-col gap-2 pt-2">
            <Button
              onClick={goToBoost}
              className="w-full bg-gradient-to-r from-amber-500 via-pink-500 to-purple-600 hover:opacity-90 text-white font-semibold h-11"
              data-testid="boost-promo-cta"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Boost My Profile
            </Button>
            <Button
              variant="ghost"
              onClick={dismiss}
              className="w-full text-gray-500 hover:text-gray-700 text-sm"
              data-testid="boost-promo-dismiss"
            >
              Maybe later
            </Button>
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer pt-1" data-testid="boost-promo-dont-show-again-row">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="rounded border-gray-300"
              data-testid="boost-promo-dont-show-again"
            />
            <span>Don&apos;t show this again</span>
            {dontShowAgain && <Check className="w-3 h-3 text-emerald-500" />}
          </label>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileBoostPromoModal;
